import axios from 'axios';
import authService from './authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const BASE_PATH = '/api/v1/snipe-hub';

const getAuthHeaders = () => {
    const token = authService.getToken();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

const handleApiError = (error) => {
    if (error.response) {
        const { status, data } = error.response;

        if (status === 400) {
            return {
                success: false,
                status,
                errorCode: data.error || 'VALIDATION_ERROR',
                message: data.errors?.[0]?.message || data.message || 'Invalid request parameters',
                errors: data.errors || []
            };
        }

        if (status === 404) {
            return {
                success: false,
                status,
                errorCode: data.error || 'GAME_NOT_FOUND',
                message: data.message || 'No AFL game found for this match.'
            };
        }

        if (status === 401) {
            return {
                success: false,
                status,
                errorCode: data.error?.code || data.error || 'UNAUTHORIZED',
                message: data.message || 'Authentication failed',
                isAuthError: true
            };
        }

        if (status === 429) {
            return {
                success: false,
                status,
                errorCode: data.error || 'RATE_LIMIT_EXCEEDED',
                message: data.message || 'Too many Snipe Hub requests. Please try again in a minute.'
            };
        }

        if (status === 503) {
            return {
                success: false,
                status,
                errorCode: data.error || 'SERVICE_UNAVAILABLE',
                message: data.message || 'Snipe Hub is temporarily unavailable. Please try again later.'
            };
        }

        return {
            success: false,
            status,
            errorCode: data.error || 'SERVER_ERROR',
            message: data.message || 'Server error. Please try again later.'
        };
    }

    return {
        success: false,
        status: null,
        errorCode: 'NETWORK_ERROR',
        message: 'Unable to connect to server. Please check your internet connection.'
    };
};

const snipeHubService = {
    getAflMatches: async () => {
        try {
            const response = await axios.get(`${API_URL}${BASE_PATH}/afl/matches`, {
                headers: getAuthHeaders()
            });

            const result = response.data;

            if (!result.success) {
                return {
                    success: false,
                    message: result.message || 'Failed to fetch matches'
                };
            }

            return {
                success: true,
                data: result.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    getAflMatch: async (matchId) => {
        try {
            const response = await axios.get(
                `${API_URL}${BASE_PATH}/afl/matches/${encodeURIComponent(matchId)}`,
                { headers: getAuthHeaders() }
            );

            const result = response.data;

            if (!result.success) {
                return {
                    success: false,
                    message: result.message || 'Failed to fetch match'
                };
            }

            return {
                success: true,
                data: result.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    // `since` (optional ISO datetime cursor): when provided, the API returns
    // only movements strictly newer than that instant — used for cheap
    // incremental polls instead of re-fetching the full feed.
    getAflPriceMovementFeed: async (matchId, since = null) => {
        try {
            const response = await axios.get(
                `${API_URL}${BASE_PATH}/afl/price-movement-feed/${encodeURIComponent(matchId)}`,
                {
                    headers: getAuthHeaders(),
                    params: since ? { since } : undefined
                }
            );

            const result = response.data;

            if (!result.success) {
                return {
                    success: false,
                    message: result.message || 'Failed to fetch price movements'
                };
            }

            return {
                success: true,
                data: result.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    }
};

export default snipeHubService;
